import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {concatLatestFrom} from '@ngrx/operators';
import {FirebaseService} from '../../../shared-utilities/services-old/firebase.service';
import {select, Store} from '@ngrx/store';
import {EMPTY, forkJoin, from, Observable, of} from 'rxjs';
import {catchError, exhaustMap, filter, map, mergeMap, switchMap, take, tap, withLatestFrom} from 'rxjs/operators';
import * as StoreSettingsActions from './settings.actions';
import * as StockManagerActions from '../../stock-manager/store/stock-manager.actions';
import * as CoreActions from '../../core/store-shared/shared.actions';
import * as NotificationActions from '../../notifications/store/notification.actions';
import * as UserStoreSelectors from '../../user-access/user-stores-store/user-store.selectors';
import * as UserSelectors from '../../user-access/user-store/user.selectors';
import * as UserAccessSelectors from '../../user-access/user-access-store/user-access.selectors';
import {
  CollectionUsersSettingsService,
} from '../../../shared/shared-services/firebase/collection-users-settings.service';
import {
  path_admin_cloud_emailer_email_test,
  path_admin_communication_maintenance,
  path_stores_storeId_settings_add_item_rules,
  path_stores_storeId_settings_disable_stock_item_rules,
  path_stores_storeId_settings_enable_stock_item_rules,
  path_stores_storeId_settings_open_order_settings,
  path_stores_storeId_settings_overrides,
  path_stores_storeId_settings_price_bands,
  path_stores_storeId_settings_stock_edit_thresholds,
  path_users_userId_settings_date_formatting,
  path_users_userId_settings_fit_to_grid,
  path_users_userId_settings_ngp_edits_preview,
  path_users_userId_settings_stock_edits_preview,
  path_users_userId_settings_stores_storeId_mailer_settings,
  path_users_userId_settings_table_navigation,
} from '../../../shared/shared-services/database-paths';
import {
  _DEFAULT_OPEN_ORDER_SETTINGS,
  IDisableStockItemRule,
  IEmailSettings,
  IEmailTester,
  IEnableStockItemRule,
  IOnHandDisableRule,
  IOpenOrderSettings,
  IStockItemEditThresholds,
  ITableNavSettings,
  TLineColour,
  TLocaleDateFormat,
} from '../../settings-new/models';
import {ISharedModalBasic} from '../../../shared/shared-models/modals/shared-modal-basic';
import {
  addDoc,
  collection as fsCollection,
  doc as fsDoc,
  DocumentReference,
  Firestore,
  onSnapshot,
  Timestamp,
} from '@angular/fire/firestore';
import {
  SharedModalBasicComponent,
} from '../../../shared/shared-components/components/shared-modal-basic/shared-modal-basic.component';
import {IconColourEnum} from '../../../shared-modules/shared-icons/models/icons-colour.enum';
import {
  CollectionStoresSettingsService,
} from '../../../shared/shared-services/firebase/collection-stores-settings.service';
import {CollectionGlobalService} from '../../../shared/shared-services/firebase/collection-global.service';
import {IStore} from '../../../shared/shared-models/store/store';
import {IPriceBand} from 'src/app/shared/shared-models/price-banding/price-band';
import {isNonEmptyObject} from 'src/app/shared/shared-utils/object/object.utils';
import {IconsEnum} from '../../../shared-modules/shared-icons/models/icons.enum';
import {IModalRef, ModalControllerService} from '../../../shared/shared-services/overlay/modal-controller.service';
import {selectEnabledRulesDoc} from './settings.selectors';
import {CollectionAdminCloudService} from '../../../shared/shared-services/firebase/collection-admin-cloud.service';
import {MaintenanceModalComponent} from '../../maintenance/components/maintenance-modal/maintenance-modal.component';
import {firebaseTimeStampToDate} from '../../../shared/shared-utils/firestore/firestore.utils';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {ErrorName} from '../../../shared/shared-components/components/global-error-display/models/global-error.enum';
import {createGlobalPaths} from '../../../shared/shared-services/database-paths.global';

@Injectable()
export class SettingsEffects {
  // ===========================================================================
  // get NGP Report Options
  // ===========================================================================
  getNGPReportOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.getNGPReportColPreviewSettings),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) => {
        const colPreviews$: Observable<{
          data: Record<number, string>;
          error: IGlobalError;
        }> = from(
          this.collectionUsersSettingsService.getDocument<
            | Record<number, string>
            | undefined
          >(String(path_users_userId_settings_ngp_edits_preview(user.id))),
        ).pipe(
          take(1),
          map((data: Record<number, string>) => ({data, error: null})),
          catchError((error: IGlobalError) => of({data: null, error})),
        );

        return forkJoin({
          colPreview: colPreviews$,
        }).pipe(
          map(({colPreview}) => {
            const errors: IGlobalError[] = [];
            if (colPreview.error) {
              errors.push({
                ...colPreview.error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: colPreview.error,
                storeIds: [store?.storeId ?? 'unknown'],
                userId: user?.id ?? 'unknown',
              });
            }
            if (errors.length > 0) {
              return StoreSettingsActions.getNGPReportColPreviewFailure({
                errors,
                store,
              });
            }

            return StoreSettingsActions.getNGPReportColPreviewSuccess({
              store,
              colPreview: colPreview.data,
            });
          }),
          catchError((error: IGlobalError) => {
            return of(
              StoreSettingsActions.getNGPReportColPreviewFailure({
                errors: [{
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store?.storeId ?? 'unknown'],
                  userId: user?.id ?? 'unknown',
                }],
                store,
              }),
            );
          }),
        );
      }),
    ),
  );

  // ===========================================================================
  // set NGP Report Preview Columns
  // ===========================================================================
  setNgpReportOptionsPreviewColumns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.setNGPReportColPreviewSettings),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      switchMap(([action, store, user]) => {
        return from(
          this.collectionUsersSettingsService.setDocument<Record<number, string>>(
            String(path_users_userId_settings_ngp_edits_preview(user.id)),
            action.columnData,
          ),
        ).pipe(
          map(() =>
            StoreSettingsActions.setNGPReportColPreviewSettingsSuccess({
              columnData: action.columnData,
              store,
            }),
          ),
          catchError((error) =>
            of(
              StoreSettingsActions.setNGPReportColPreviewSettingsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
                store,
              }),
            ),
          ),
        );
      }),
    ),
  );

  // ===========================================================================
  // set NGP Report Table Navigation Settings
  // ===========================================================================
  setTableNavSettingsInFirebase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.setTableNavSettings),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) => {
        return from(
          this.collectionUsersSettingsService.setDocument<ITableNavSettings>(
            String(path_users_userId_settings_table_navigation(user.id)),
            action.settings,
          ),
        ).pipe(
          map(() =>
            StoreSettingsActions.setTableNavSettingsSuccess({
              settings: action.settings,
              store,
            }),
          ),
          catchError((error) =>
            of(
              StoreSettingsActions.setTableNavSettingsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
                store,
              }),
            ),
          ),
        );
      }),
    ),
  );

  // ===========================================================================
  // get Table Navigation Settings
  // ===========================================================================
  getTableSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.getTableSettings),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        from(
          this.collectionUsersSettingsService.getDocument<{
            value: boolean;
          }>(path_users_userId_settings_fit_to_grid(user.id)),
        ).pipe(
          mergeMap((fitToGrid: { value: boolean }) =>
            from(
              this.collectionUsersSettingsService.getDocument<ITableNavSettings>(
                path_users_userId_settings_table_navigation(user.id),
              ),
            ).pipe(
              take(1),
              map((data: ITableNavSettings) => {
                return StoreSettingsActions.getTableSettingsSuccess({
                  tableNavSettings: data,
                  fitToGrid: fitToGrid.value ?? true,
                  store,
                });
              }),
              catchError((error) => {
                return of(
                  StoreSettingsActions.getTableSettingsFailure({
                    error: {
                      ...error,
                      action: JSON.stringify(action),
                      actionType: action.type,
                      originalError: error,
                      storeIds: [store.storeId],
                      userId: user.id,
                    },
                    store,
                  }),
                );
              }),
            ),
          ),
          catchError((error) => {
            console.error(`Error in getOrCreateFitToGridDocument:`, error);
            return of(
              StoreSettingsActions.getTableSettingsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
                store,
              }),
            );
          }),
        ),
      ),
    ),
  );

  getDisabledRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.getItemDisablingRules),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectStores)),
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, stores, store, user]) =>
        from(stores).pipe(
          mergeMap((store) =>
            forkJoin([
              this.collectionStoresSettingsService.getDocument<IDisableStockItemRule>(
                path_stores_storeId_settings_disable_stock_item_rules(store.storeId),
              ), this.collectionStoresSettingsService.getDocument<IEnableStockItemRule>(
                path_stores_storeId_settings_enable_stock_item_rules(store.storeId),
              )],
            )
              .pipe(
                mergeMap(([disableRulesDoc, enableRulesDoc]) =>
                  from([StoreSettingsActions.getItemDisablingRulesSuccess({
                    rulesDoc: disableRulesDoc,
                    store,
                  }), StoreSettingsActions.getItemEnablingRulesSuccess({
                    rulesDoc: enableRulesDoc,
                    store,
                  })]),
                ),
                catchError((error) =>
                  from([
                      StoreSettingsActions.getItemDisablingRulesFailure({
                        error: {
                          ...error,
                          action: JSON.stringify(action),
                          actionType: action.type,
                          originalError: error,
                          storeIds: [store.storeId],
                          userId: user.id,
                        },
                        store,
                      }),
                      StoreSettingsActions.getItemEnablingRulesFailure({
                        error: {
                          ...error,
                          action: JSON.stringify(action),
                          actionType: action.type,
                          originalError: error,
                          storeIds: [store.storeId],
                          userId: user.id,
                        },
                        store,
                      }),
                    ],
                  ),
                ),
              ),
          ),
        ),
      ),
    ),
  );

  getLineColour$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.getLineColour),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectStores)),
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, stores, store, user]) =>
        from(stores).pipe(
          mergeMap((store) =>
            from(
              this.firebaseService.getStoreDataDoc('line_colours', store.storeId),
            ).pipe(
              map((colour: TLineColour) =>
                StoreSettingsActions.getLineColourSuccess({colour, store}),
              ),
              catchError((error) =>
                of(StoreSettingsActions.getLineColourFailure({
                  error: {
                    ...error,
                    action: JSON.stringify(action),
                    actionType: action.type,
                    originalError: error,
                    storeIds: [store.storeId],
                    userId: user.id,
                  },
                  store,
                })),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  getStockManagerColPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.getStockManagerColPreviewSettings),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        from(
          this.collectionUsersSettingsService.getDocument<Record<number, string>>(path_users_userId_settings_stock_edits_preview(user.id)),
        ).pipe(
          map((colPreview: Record<number, string>) =>
            StoreSettingsActions.getStockManagerColPreviewSettingsSuccess({
              colPreview,
              store,
            }),
          ),
          catchError((error) =>
            of(
              StoreSettingsActions.getStockManagerColPreviewSettingsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  setStockManagerColPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.setStockManagerPreviewColumns),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      switchMap(([action, store, user]) =>
        from(
          this.collectionUsersSettingsService.setDocument<Record<number, string>>(
            path_users_userId_settings_stock_edits_preview(user.id),
            action.columnData,
          ),
        ).pipe(
          mergeMap(() => [
            StoreSettingsActions.setStockManagerPreviewColumnsSuccess({
              columnData: action.columnData,
              store,
            }),
            StockManagerActions.setStockManagerPreviewColumnsSuccess({
              columnData: action.columnData,
            }),
          ]),
          catchError((error) =>
            of(
              StoreSettingsActions.setStockManagerPreviewColumnsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
                store,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  getStockEditThresholds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.getStockEditThresholds),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        from(
          this.collectionStoresSettingsService.getDocument<IStockItemEditThresholds>(
            path_stores_storeId_settings_stock_edit_thresholds(store.storeId),
          ),
        ).pipe(
          map((thresholds: IStockItemEditThresholds) =>
            StoreSettingsActions.getStockEditThresholdsSuccess({
              thresholds,
              storeId: store.storeId,
            }),
          ),
          catchError((error) =>
            of(StoreSettingsActions.getStockEditThresholdsFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store.storeId],
                userId: user.id,
              },
            })),
          ),
        ),
      ),
    ),
  );

  // runs once, fetches bands for every store, falls back to global, persists to store doc
  getPriceBands$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.getPriceBands),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectStores)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, stores, user]) =>
        from(stores ?? []).pipe(
          mergeMap((store) => {
            const storePath = path_stores_storeId_settings_price_bands(store.storeId);
            const globalPath = createGlobalPaths().priceBands();

            return from(
              this.collectionStoresSettingsService.getDocument<Record<number, IPriceBand>>(storePath),
            ).pipe(
              catchError(() => of(null)),
              switchMap((storeBands) => {
                if (isNonEmptyObject(storeBands)) {
                  return of(
                    StoreSettingsActions.getPriceBandsSuccess({
                      priceBands: storeBands,
                      storeId: store.storeId,
                    }),
                  );
                }

                // fallback to global, then persist to the store’s path
                return from(
                  this.collectionGlobalService.getDocument<Record<number, IPriceBand>>(globalPath),
                ).pipe(
                  switchMap((globalBands) =>
                    from(this.collectionStoresSettingsService.setDocument(storePath, globalBands)).pipe(
                      map(() =>
                        StoreSettingsActions.getPriceBandsSuccess({
                          priceBands: globalBands,
                          storeId: store.storeId,
                        }),
                      ),
                    ),
                  ),
                  catchError((error) =>
                    of(
                      StoreSettingsActions.getPriceBandsFailure({
                        error: {
                          ...error,
                          note: 'Failed to fetch or persist global price bands',
                          action: JSON.stringify(action),
                          actionType: action.type,
                          originalError: error,
                          storeIds: [store.storeId],
                          userId: user.id,
                        },
                      }),
                    ),
                  ),
                );
              }),
              catchError((error) =>
                of(
                  StoreSettingsActions.getPriceBandsFailure({
                    error: {
                      ...error,
                      note: 'Failed to fetch store price bands',
                      action: JSON.stringify(action),
                      actionType: action.type,
                      originalError: error,
                      storeIds: [store.storeId],
                      userId: user.id,
                    },
                  }),
                ),
              ),
            );
          }),
        ),
      ),
    ),
  );

  getUserEmailSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.getUserEmailSettings),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        from(
          this.collectionUsersSettingsService.getDocument<IEmailSettings>(
            path_users_userId_settings_stores_storeId_mailer_settings(
              user.id,
              store.storeId,
            ),
          ),
        ).pipe(
          map((settings: IEmailSettings) =>
            StoreSettingsActions.getUserEmailSettingsSuccess({
              settings,
              user,
              store,
            }),
          ),
          catchError((error) =>
            of(StoreSettingsActions.getUserEmailSettingsFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store.storeId],
                userId: user.id,
              },
            })),
          ),
        ),
      ),
    ),
  );

  getAllUserEmailSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.getAllUserEmailSettings),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) => {
        if (!isNonEmptyObject(store) || !isNonEmptyObject(user)) {
          return of(
            StoreSettingsActions.getAllUserEmailSettingsFailure({
              error: {
                action: JSON.stringify(action),
                actionType: action.type,
                message: 'No store or user selected.',
                name: ErrorName.ActionFailedError,
                storeIds: [store.storeId],
                userId: user.id,
              },
            }),
          );
        }

        return from(
          this.collectionUsersSettingsService.getDocument<IEmailSettings>(
            path_users_userId_settings_stores_storeId_mailer_settings(
              user.id,
              store.storeId,
            ),
          ),
        ).pipe(
          mergeMap((userSettings: IEmailSettings) => {
            const currentUser = {
              [user.id]: userSettings || ({} as IEmailSettings),
            };

            return this.collectionUsersSettingsService
              .getAllMailerSettings(store.storeId)
              .pipe(
                map((settings) => {
                  return StoreSettingsActions.getAllUserEmailSettingsSuccess({
                    usersEmailSettings: {...currentUser, ...settings},
                    store,
                  });
                }),
              );
          }),
          catchError((error) => {
            console.error('Error fetching user email settings:', error);
            return of(
              StoreSettingsActions.getAllUserEmailSettingsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
              }),
            );
          }),
        );
      }),
    ),
  );

  showModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StoreSettingsActions.showModal),
        tap(async ({modalProps}) => {
          const modalController =
            await this.modalController.create({
              component: SharedModalBasicComponent,
              componentProps: modalProps,
              cssClass: ['shared-basic-modal-css-medium'],
            });
          await modalController.present();
        }),
      ),
    {dispatch: false},
  );

  testEmailSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        StoreSettingsActions.testEmailSuccess,
        StoreSettingsActions.testEmailFailure,
      ),
      mergeMap((_) => of(StoreSettingsActions.getAllUserEmailSettings())),
    ),
  );

  getAllUserSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.getAllUserSettings),
      mergeMap(() => [
        StoreSettingsActions.getTableSettings(),
        StoreSettingsActions.getNGPReportColPreviewSettings(),
        StoreSettingsActions.getStockManagerColPreviewSettings(),
        StoreSettingsActions.getOpenOrderSettings(),
      ]),
    ),
  );

  updateAllUserSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.updateAllUserSettings),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) => {
        const {
          fitToGrid,
          ngpPreviewColumns,
          stockPreviewColumns,
          tableNavSettings,
        } = action;
        return forkJoin([
          from(
            this.collectionUsersSettingsService.setDocumentNoMerge(
              path_users_userId_settings_fit_to_grid(user.id),
              {value: fitToGrid},
            ),
          ),
          from(
            this.collectionUsersSettingsService.setDocumentNoMerge(
              path_users_userId_settings_ngp_edits_preview(user.id),
              ngpPreviewColumns,
            ),
          ),
          from(
            this.collectionUsersSettingsService.setDocumentNoMerge(
              path_users_userId_settings_stock_edits_preview(user.id),
              stockPreviewColumns,
            ),
          ),
          from(
            this.collectionUsersSettingsService.setDocumentNoMerge(
              path_users_userId_settings_table_navigation(user.id),
              tableNavSettings,
            ),
          ),
        ]).pipe(
          map(() =>
            StoreSettingsActions.updateAllUserSettingsSuccess({
              fitToGrid,
              ngpPreviewColumns,
              stockPreviewColumns,
              tableNavSettings,
            }),
          ),
          catchError((error) =>
            of(StoreSettingsActions.updateAllUserSettingsFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store.storeId],
                userId: user.id,
              },
            })),
          ),
        );
      }),
    ),
  );

  getAllUserStoreSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.getAllUserStoreSettings),
      withLatestFrom(
        this.store.pipe(select(UserAccessSelectors.selectUserAccess)),
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([_, userAccess, selectedStore]) => {
        const userAdminStores = userAccess?.adminAccessStores || [];
        const canAccessStore = userAdminStores.includes(
          selectedStore?.storeId || '',
        );
        if (!canAccessStore) return [];
        return [
          StoreSettingsActions.getLineColour(),
          StoreSettingsActions.getAddStockItemRules(),
          StoreSettingsActions.getItemDisablingRules(),
          StoreSettingsActions.getStockEditThresholds(),
          StoreSettingsActions.getPriceBands(),
          StoreSettingsActions.getAllUserEmailSettings(),
          StoreSettingsActions.getOpenOrderSettings(),
          CoreActions.getColleagues(),
        ];
      }),
    ),
  );

  getAllColleagueEmailSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.getColleaguesSuccess),
      map(() => StoreSettingsActions.getAllUserEmailSettings()),
    ),
  );

  updateOnHandSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.updateOnHandSettings),
      withLatestFrom(
        this.store.pipe(select(selectEnabledRulesDoc)),
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, enableRulesDoc, store, user]) => {
        const {ruleType, onHandZero, onHandNotZero} = action;
        const settings: {
          onHandZero: IOnHandDisableRule,
          onHandNotZero: IOnHandDisableRule,
        } = {
          onHandZero: {
            descPrefix: onHandZero.descPrefix ?? '',
            descSuffix: onHandZero.descSuffix ?? '',
            descPrefixArray: onHandZero.descPrefixArray ?? [],
            descSuffixArray: onHandZero.descSuffixArray ?? [],
            lineColour: onHandZero.lineColour ?? 0,
            onHoldCode: onHandZero.onHoldCode ?? 0,
          },
          onHandNotZero: {
            descPrefix: onHandNotZero.descPrefix ?? '',
            descSuffix: onHandNotZero.descSuffix ?? '',
            descPrefixArray: onHandNotZero.descPrefixArray ?? [],
            descSuffixArray: onHandNotZero.descSuffixArray ?? [],
            lineColour: onHandNotZero.lineColour ?? 0,
            onHoldCode: onHandNotZero.onHoldCode ?? 0,
          },
        };

        if (ruleType === 'enable') {
          delete settings.onHandNotZero.descPrefix;
          delete settings.onHandNotZero.descSuffix;
          delete settings.onHandZero.descPrefix;
          delete settings.onHandZero.descSuffix;
        } else {
          delete settings.onHandNotZero.descPrefixArray;
          delete settings.onHandNotZero.descSuffixArray;
          delete settings.onHandZero.descPrefixArray;
          delete settings.onHandZero.descSuffixArray;
          const enableRulesDocCopy: IEnableStockItemRule = structuredClone(enableRulesDoc);

          // Filter out empty strings before adding to the arrays
          const validZeroPrefix = settings.onHandZero.descPrefix?.trim() || '';
          const validNotZeroPrefix = settings.onHandNotZero.descPrefix?.trim() || '';
          const validZeroSuffix = settings.onHandZero.descSuffix?.trim() || '';
          const validNotZeroSuffix = settings.onHandNotZero.descSuffix?.trim() || '';

          const prefixesToAdd = [validZeroPrefix, validNotZeroPrefix].filter((prefix: string) => prefix !== '');
          const suffixesToAdd = [validZeroSuffix, validNotZeroSuffix].filter((suffix: string) => suffix !== '');

          enableRulesDocCopy.descPrefixArray = Array.from(new Set([
            ...(enableRulesDoc.descPrefixArray || []),
            ...prefixesToAdd,
          ]));

          enableRulesDocCopy.descSuffixArray = Array.from(new Set([
            ...(enableRulesDoc.descSuffixArray || []),
            ...suffixesToAdd,
          ]));

          void this.collectionStoresSettingsService.setDocument<IEnableStockItemRule>(
            path_stores_storeId_settings_enable_stock_item_rules(store.storeId),
            enableRulesDocCopy,
          );
        }
        return forkJoin([
          from(
            this.collectionStoresSettingsService.setDocument<{
              onHandZero: IOnHandDisableRule,
              onHandNotZero: IOnHandDisableRule,
            }>(
              ruleType !== 'enable' ? path_stores_storeId_settings_disable_stock_item_rules(
                store.storeId,
              ) : path_stores_storeId_settings_enable_stock_item_rules(store.storeId),
              settings,
            ),
          ),
        ]).pipe(
          map(() => StoreSettingsActions.updateOnHandSettingsSuccess()),
          catchError((error) =>
            of(StoreSettingsActions.updateOnHandSettingsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
              }),
            ),
          ),
        );
      }),
    ),
  );

  updateEnablingSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.updateEnablingSettings),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) => {
        const {formData} = action;
        const enableData: IEnableStockItemRule = {
          descPrefixArray: formData.descPrefixArray ?? [],
          descSuffixArray: formData.descSuffixArray ?? [],
          lineColour: formData.lineColour ?? 0,
          onHoldCode: formData.onHoldCode ?? 0,
        };

        return from(
          this.collectionStoresSettingsService.setDocument<IEnableStockItemRule>(
            path_stores_storeId_settings_enable_stock_item_rules(store.storeId),
            enableData,
          ),
        ).pipe(
          map(() => StoreSettingsActions.updateOnHandSettingsSuccess()),
          catchError((error) =>
            of(StoreSettingsActions.updateOnHandSettingsFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store.storeId],
                userId: user.id,
              },
            })),
          ),
        );
      }),
    ),
  );

  updateStockEditThresholds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.updateStockEditThresholds),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        from(
          this.collectionStoresSettingsService.setDocument<IStockItemEditThresholds>(
            path_stores_storeId_settings_stock_edit_thresholds(store.storeId),
            action.thresholds,
          ),
        ).pipe(
          map(() => StoreSettingsActions.updateStockEditThresholdsSuccess()),
          catchError((error) =>
            of(StoreSettingsActions.updateStockEditThresholdsFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store.storeId],
                userId: user.id,
              },
            })),
          ),
        ),
      ),
    ),
  );


  updateOpenOrderSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.updateOpenOrderSettings),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([{openOrderSettings}, store]) =>
        from(
          this.collectionStoresSettingsService.setDocument<IOpenOrderSettings>(
            path_stores_storeId_settings_open_order_settings(store.storeId),
            openOrderSettings,
          ),
        ).pipe(
          map(() => StoreSettingsActions.updateOpenOrderSettingsSuccess({openOrderSettings, store})),
          catchError((error) =>
            of(StoreSettingsActions.updateOpenOrderSettingsFailure({error})),
          ),
        ),
      ),
    ),
  );
  setSupplierOverRide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.setSupplierOverride),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        from(
          this.collectionStoresSettingsService.setDocument<{ orderBlankSupplierCode: boolean }>(
            path_stores_storeId_settings_overrides(store.storeId),
            {orderBlankSupplierCode: action.supplierOverRide},
          ),
        ).pipe(
          map(() => StoreSettingsActions.setSupplierOverrideSuccess({
            supplierOverRide: action.supplierOverRide,
            store,
          })),
          catchError((error) =>
            of(NotificationActions.setGlobalErrorOccurrence({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store.storeId],
                userId: user.id,
              },
            })),
          ),
        ),
      ),
    ),
  );

  setShelfTalkerOverRide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.setShelfTalkerOverride),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        from(
          this.collectionStoresSettingsService.setDocument<{ shelfTalkersEnabled: boolean }>(
            path_stores_storeId_settings_overrides(store.storeId),
            {shelfTalkersEnabled: action.shelfTalkerOverRide},
          ),
        ).pipe(
          map(() => StoreSettingsActions.setShelfTalkerOverrideSuccess({
            shelfTalkerOverRide: action.shelfTalkerOverRide,
            store,
          })),
          catchError((error) =>
            of(NotificationActions.setGlobalErrorOccurrence({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store.storeId],
                userId: user.id,
              },
            })),
          ),
        ),
      ),
    ),
  );

  getOverRide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.getOverrides),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        from(
          this.collectionStoresSettingsService.getDocument<{
            orderBlankSupplierCode?: boolean;
            shelfTalkersEnabled?: boolean
          }>(
            path_stores_storeId_settings_overrides(action.store.storeId),
          ),
        ).pipe(
          map((overrides) => {
            const orderBlankSupplierCode = overrides?.orderBlankSupplierCode ?? false;
            const shelfTalkersEnabled = overrides?.shelfTalkersEnabled ?? true;
            return StoreSettingsActions.getOverridesSuccess({
              overrides: {orderBlankSupplierCode, shelfTalkersEnabled},
              store: action.store,
            });
          }),
          catchError((error) =>
            of(NotificationActions.setGlobalErrorOccurrence({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store.storeId],
                userId: user.id,
              },
            })),
          ),
        ),
      ),
    ),
  );

  testEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.testEmail),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) => {
        const payload = {
          storeId: action.storeId,
          userId: action.userId,
          recipient: 'test-receive@techodactyl.co.za',
        };
        return from(
          addDoc(fsCollection(this.firestore, path_admin_cloud_emailer_email_test()), payload),
        ).pipe(
          mergeMap((docRef: DocumentReference) => {
            return new Observable<IEmailTester>(subscriber => {
              const docPath = path_users_userId_settings_stores_storeId_mailer_settings(
                action.userId,
                action.storeId,
              );
              const unsubscribe = onSnapshot(
                fsDoc(this.firestore, docPath),
                (doc) => {
                  if (doc.exists()) {
                    const data = doc.data() as IEmailTester;
                    subscriber.next(data);
                  }
                },
                (error) => {
                  subscriber.error(error);
                },
              );
              return (): void => {
                unsubscribe();
              };
            }).pipe(
              filter(
                (doc: IEmailTester | undefined) =>
                  !!doc && (doc.status === 'SENT' || doc.status === 'FAILED'),
              ),
              take(1),
              mergeMap((doc) => {
                let modalProps: ISharedModalBasic;
                if (doc.status === 'SENT') {
                  modalProps = {
                    buttonAccept: false,
                    buttonAcceptText: '',
                    buttonClose: true,
                    buttonCloseCross: false,
                    buttonCloseText: 'Ok',
                    modalTitle: 'Email Test Successful',
                    modalTitleIcon: IconsEnum.radioBoxMarked,
                    modalTitleIconColor: IconColourEnum.green,
                    contextHeading:
                      'Your Email Settings Are Working As Desired.',
                    contextDescription: [
                      'The server successfully used the configured email settings.',
                    ],
                  };
                } else {
                  modalProps = {
                    buttonAccept: false,
                    buttonAcceptText: '',
                    buttonClose: true,
                    buttonCloseCross: false,
                    buttonCloseText: 'Ok',
                    modalTitle: 'Email Test Failed',
                    modalTitleIcon: IconsEnum.bugOutline,
                    modalTitleIconColor: IconColourEnum.red,
                    contextHeading:
                      doc.errorMsg.code ||
                      'An error occurred during the email test.',
                    contextDescription: [
                      doc.errorMsg.message ||
                      'No additional information provided. Please contact Techodactyl Support.',
                    ],
                  };
                }
                return of(
                  StoreSettingsActions.showModal({modalProps}),
                  StoreSettingsActions.testEmailSuccess({
                    status: doc.status,
                  }),
                );
              }),
              catchError((error) => {
                console.error(error);
                const errorModalProps: ISharedModalBasic = {
                  buttonAccept: false,
                  buttonAcceptText: '',
                  buttonClose: true,
                  buttonCloseCross: false,
                  buttonCloseText: 'Ok',
                  modalTitle: 'Email Test Error',
                  modalTitleIcon: IconsEnum.bug,
                  modalTitleIconColor: IconColourEnum.red,
                  contextHeading: 'An unexpected error occurred.',
                  contextDescription: [
                    'We were unable to process the email test. Please try again later.',
                    error.error || 'Unknown error.',
                  ],
                };
                return of(
                  StoreSettingsActions.showModal({
                    modalProps: errorModalProps,
                  }),
                  StoreSettingsActions.testEmailFailure({
                    error: {
                      ...error,
                      action: JSON.stringify(action),
                      actionType: action.type,
                      originalError: error,
                      storeIds: [store.storeId],
                      userId: user.id,
                    },
                  }),
                );
              }),
            );
          }),
          catchError((error) => {
            console.error(error);
            const errorModalProps: ISharedModalBasic = {
              buttonAccept: false,
              buttonAcceptText: '',
              buttonClose: true,
              buttonCloseCross: false,
              buttonCloseText: 'Ok',
              modalTitle: 'Firestore Error',
              modalTitleIcon: IconsEnum.bug,
              modalTitleIconColor: IconColourEnum.red,
              contextHeading: 'Unable to create the test document.',
              contextDescription: [
                'An error occurred while trying to initiate the email test.',
                error.error || 'Unknown error.',
              ],
            };
            return of(
              StoreSettingsActions.showModal({modalProps: errorModalProps}),
              StoreSettingsActions.testEmailFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
              }),
            );
          }),
        );
      }),
    ),
  );
  handleUserSettingsUpdateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.updateAllUserSettingsSuccess),
      mergeMap(() => {
        const modalProps: ISharedModalBasic = {
          buttonAccept: false,
          buttonAcceptText: '',
          buttonClose: true,
          buttonCloseCross: false,
          buttonCloseText: 'Ok',
          modalTitle: 'Settings Saved',
          modalTitleIcon: IconsEnum.emailCheckOutline,
          modalTitleIconColor: IconColourEnum.green,
          contextHeading: 'Your settings have been updated successfully.',
          contextDescription: ['All changes have been saved.'],
        };
        return [
          StoreSettingsActions.showModal({modalProps}),
          StoreSettingsActions.getAllUserSettings(),
        ];
      }),
    ),
  );
  handleUserSettingsUpdateFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.updateAllUserSettingsFailure),
      mergeMap(({error}) => {
        const modalProps: ISharedModalBasic = {
          buttonAccept: false,
          buttonAcceptText: '',
          buttonClose: true,
          buttonCloseCross: false,
          buttonCloseText: 'Ok',
          modalTitle: 'Error Saving Settings',
          modalTitleIcon: IconsEnum.emailCheckOutline,
          modalTitleIconColor: IconColourEnum.red,
          contextHeading: 'Failed to save your settings.',
          contextDescription: [error?.message || 'An unexpected error occurred.'],
        };
        return [
          StoreSettingsActions.showModal({modalProps}),
          StoreSettingsActions.getAllUserSettings(),
        ];
      }),
    ),
  );
  handleOnHandSettingsUpdateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.updateOnHandSettingsSuccess),
      mergeMap(() => {
        const modalProps: ISharedModalBasic = {
          buttonAccept: false,
          buttonAcceptText: '',
          buttonClose: true,
          buttonCloseCross: false,
          buttonCloseText: 'Ok',
          modalTitle: 'Store Settings Saved',
          modalTitleIcon: IconsEnum.emailCheckOutline,
          modalTitleIconColor: IconColourEnum.green,
          contextHeading: 'Your store settings have been updated successfully.',
          contextDescription: ['All changes have been saved.'],
        };
        return [
          StoreSettingsActions.showModal({modalProps}),
          StoreSettingsActions.getAllUserStoreSettings(),
        ];
      }),
    ),
  );
  handleOnHandSettingsUpdateFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.updateOnHandSettingsFailure),
      mergeMap(({error}) => {
        const modalProps: ISharedModalBasic = {
          buttonAccept: false,
          buttonAcceptText: '',
          buttonClose: true,
          buttonCloseCross: false,
          buttonCloseText: 'Ok',
          modalTitle: 'Error Saving Store Settings',
          modalTitleIcon: IconsEnum.emailCheckOutline,
          modalTitleIconColor: IconColourEnum.red,
          contextHeading: 'Failed to save your store settings.',
          contextDescription: [error?.message || 'An unexpected error occurred.'],
        };
        return [
          StoreSettingsActions.showModal({modalProps}),
          StoreSettingsActions.getAllUserStoreSettings(),
        ];
      }),
    ),
  );
  handleStockEditThresholdsUpdateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.updateStockEditThresholdsSuccess),
      mergeMap(() => {
        const modalProps: ISharedModalBasic = {
          buttonAccept: false,
          buttonAcceptText: '',
          buttonClose: true,
          buttonCloseCross: false,
          buttonCloseText: 'Ok',
          modalTitle: 'Store Settings Saved',
          modalTitleIcon: IconsEnum.emailCheckOutline,
          modalTitleIconColor: IconColourEnum.green,
          contextHeading: 'Your store settings have been updated successfully.',
          contextDescription: ['All changes have been saved.'],
        };
        return [
          StoreSettingsActions.showModal({modalProps}),
          StoreSettingsActions.getAllUserStoreSettings(),
        ];
      }),
    ),
  );
  handleStockEditThresholdsUpdateFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.updateStockEditThresholdsFailure),
      mergeMap(({error}) => {
        const modalProps: ISharedModalBasic = {
          buttonAccept: false,
          buttonAcceptText: '',
          buttonClose: true,
          buttonCloseCross: false,
          buttonCloseText: 'Ok',
          modalTitle: 'Error Updating Stock Edit Thresholds',
          modalTitleIcon: IconsEnum.emailCheckOutline,
          modalTitleIconColor: IconColourEnum.red,
          contextHeading: 'Failed to update stock edit thresholds.',
          contextDescription: [error?.message || 'An unexpected error occurred.'],
        };
        return [
          StoreSettingsActions.showModal({modalProps}),
          StoreSettingsActions.getAllUserStoreSettings(),
        ];
      }),
    ),
  );
  // ===========================================================================
  // get Open Order Settings
  // ===========================================================================

  getOpenOrderSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.getOpenOrderSettings),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectLocalStoresByFeature)),
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, storesByFeature, selectedStore, user]) => {
        const autoOrderStores = storesByFeature?.['auto-ordering'] ?? [];
        if (autoOrderStores.length === 0) return EMPTY;
        return from(autoOrderStores).pipe(
          mergeMap((autoOrderStore: IStore) =>
            from(
              this.collectionUsersSettingsService.getDocument<IOpenOrderSettings>(
                path_stores_storeId_settings_open_order_settings(autoOrderStore.storeId),
              ),
            ).pipe(
              take(1),
              tap(() => {
                this.store.dispatch(StoreSettingsActions.getOverrides({store: autoOrderStore}));
              }),
              map((settings: IOpenOrderSettings) =>
                StoreSettingsActions.getOpenOrderSettingsSuccess({
                  openOrderSettings: isNonEmptyObject(settings)
                    ? settings
                    : _DEFAULT_OPEN_ORDER_SETTINGS,
                  store: autoOrderStore,
                }),
              ),
              catchError((error) =>
                of(
                  StoreSettingsActions.getOpenOrderSettingsFailure({
                    error: {
                      ...error,
                      action: JSON.stringify(action),
                      actionType: action.type,
                      originalError: error,
                      storeIds: [autoOrderStore?.storeId ?? 'unknown'],
                      userId: user?.id ?? 'unknown',
                    },
                    store: autoOrderStore,
                  }),
                  NotificationActions.setGlobalErrorOccurrence({
                    error: {
                      ...error,
                      action: JSON.stringify(action),
                      actionType: action.type,
                      originalError: error,
                      storeIds: [autoOrderStore?.storeId ?? 'unknown'],
                      userId: user?.id ?? 'unknown',
                    },
                  }),
                ),
              ),
            ),
          ),
        );
      }),
    ),
  );

  getIsStoreUnderMaintenance$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StoreSettingsActions.getIsStoreUnderMaintenance),
        switchMap(() =>
          this.collectionAdminCloudService
            .watchCollection<{ endDate: Timestamp }>(path_admin_communication_maintenance()),
        ),
        tap((document: Record<string, string | number | boolean | Timestamp>) => {
          if (document?.endDate) {
            if (!this.maintenanceModal) {
              void (async (): Promise<void> => {
                this.maintenanceModal = await this.modalController.create({
                  component: MaintenanceModalComponent,
                  componentProps: {
                    endDate: firebaseTimeStampToDate(document.endDate as Timestamp),
                  },
                  backdropDismiss: false,
                  cssClass: ['maintenance-modal'],
                });
                await this.maintenanceModal.present();
              })();
            }
          } else {
            if (this.maintenanceModal) {
              void this.maintenanceModal.dismiss();
              this.maintenanceModal = undefined;
              window.location.reload();
            }
          }
        }),
        catchError((error) => {
          console.error('Maintenance monitoring failed', error);
          return of(NotificationActions.setGlobalErrorOccurrence({
            error: {
              name: 'getIsStoreUnderMaintenance',
              message: 'Failed to Access Maintenance Status',
              actionType: StoreSettingsActions.getIsStoreUnderMaintenance.type,
              originalError: error,
            },
          }));
        }),
      ),
    {dispatch: false},
  );

  getAddStockItemRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.getAddStockItemRules),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        from(
          this.collectionStoresSettingsService.getDocument<{ prefix: string; suffix: string; digits: number }>(
            path_stores_storeId_settings_add_item_rules(store.storeId),
          ),
        ).pipe(
          map((rules: {
            prefix: string
            suffix: string
            digits: number
          }) => {
            return StoreSettingsActions.getAddStockItemRulesSuccess({
              store, prefix: rules?.prefix ?? null, suffix: rules?.suffix ?? null, digits: rules?.digits ?? null,
            });
          }),
          catchError((error) =>
            of(NotificationActions.setGlobalErrorOccurrence({
              error: {
                ...error,
                name: 'getAddStockItemRules$',
                message: 'Get Add Stock Item Rules',
                actionType: action.type,
                originalError: error,
                action,
              },
            })),
          ),
        ),
      ),
    ),
  );

  setAddStockItemRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StoreSettingsActions.setAddStockItemRules),
      concatLatestFrom(() => [
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserSelectors.selectUser)),
      ]),
      exhaustMap(([action, store, user]) => {
        const {prefix, suffix, digits} = action;
        return from(
          this.collectionStoresSettingsService.setDocument<{ prefix: string; suffix: string; digits: number }>(
            path_stores_storeId_settings_add_item_rules(store.storeId),
            {prefix, suffix, digits},
          ),
        ).pipe(
          map(() =>
            StoreSettingsActions.setAddStockItemRulesSuccess({
              storeId: store.storeId,
              prefix,
              suffix,
              digits,
            }),
          ),
          catchError(error =>
            of(
              NotificationActions.setGlobalErrorOccurrence({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
              }),
            ),
          ),
        );
      }),
    ),
  );

  private maintenanceModal?: IModalRef;

  constructor(
    private actions$: Actions,
    private firebaseService: FirebaseService,
    private collectionUsersSettingsService: CollectionUsersSettingsService,
    private collectionStoresSettingsService: CollectionStoresSettingsService,
    private collectionGlobalService: CollectionGlobalService,
    private modalController: ModalControllerService,
    private collectionAdminCloudService: CollectionAdminCloudService,
    private firestore: Firestore,
    private readonly store: Store,
  ) {
  }

}
